import { createSlice } from '@reduxjs/toolkit';

import { StatusTransport } from '@pages/hubs/Garage/enum/statusTransport.enums';
import { createFormStepper, formStepper } from '@pages/hubs/Pets/const/create-form-stepper';
import { CreatePetStepsEnum } from '@pages/hubs/Pets/enum/create-pet-steps-enum';
import { PetTypeEnum } from '@pages/hubs/Pets/enum/pet-type-enum';
import { WayToPetInfo } from '@pages/hubs/Pets/enum/way-to-pet-info';
import { acceptedPetWS, addPetWS, getPets, updatePetWS } from '@pages/hubs/Pets/store/pets-thunk';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import type {
  GetHubsParams,
  PetsInsurance,
  PetsVaccine,
  RootPetItemsDataStep,
  RootPetsData,
} from './pets-types';
import type { FilterParams, RootMeta } from '@pages/hubs/Garage/store/types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AssignPeoplePermissionsEnum } from '@shared/enums/assignPeoplePermissions.enum';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { MediaType } from '@shared/models/media.model';

const initialState = {
  isLoadingPets: false,
  pets: {
    data: [] as RootPetsData[],
    meta: {} as RootMeta,
  },
  currentPet: {} as RootPetsData,
  isListOver: false as boolean | number,
  isEmptyPetsWithFilter: false,
  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  editMode: false,

  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectUser: null as AssignPeopleSelectValueModel | null,
  },

  createPet: {
    formStepper: formStepper as any,
    currentStep: CreatePetStepsEnum.preStepPetType,
    petType: PetTypeEnum.Custom,
    draftItem: null as RootPetsData | null,
    isCanChangeCurrentStep: true,
    wayToEnterPetInfo: WayToPetInfo.manual,
  },
};

type InitialStateType = typeof initialState;
export const petsSlice = createSlice({
  name: 'petsSlice',
  initialState: initialState as InitialStateType,
  reducers: {
    // ------------------ common ------------------
    setLoadingPets(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingPets = action.payload.isLoading;
    },
    clearPetsFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },
    resetPetState() {
      return initialState;
    },
    // ------------------ main page ------------------
    setPetsFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    removeCurrentPet(state, action: PayloadAction<{ petID: number }>) {
      const index = state.pets.data.findIndex((pet) => pet.id === action.payload.petID);
      if (index !== -1) state.pets.data.splice(index, 1);

      if (
        !state.pets.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyPetsWithFilter = true;
      }
    },
    setPetDataWithNewPosition(state, action: PayloadAction<{ newPositionData: RootPetsData[] }>) {
      state.pets.data = action.payload.newPositionData;
    },
    isEmptyDataPetsWithFilter(
      state,
      action: PayloadAction<{
        filters: Pick<GetHubsParams, 'shared_filter' | 'query'>;
        petsData: RootPetsData[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyPetsWithFilter = (!!shared_filter?.length || !!query) && !action.payload.petsData.length;
    },
    clearPetsState(state) {
      state.pets = initialState.pets;
    },
    // ------------------ view pet ------------------
    setCurrentPet(state, action: PayloadAction<{ pet: RootPetsData }>) {
      state.currentPet = action.payload.pet;
    },
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.editMode = action.payload.editMode;
    },
    // ------------------ pet insurance ------------------
    addCreatedPetInsuranceToList(state, action: PayloadAction<PetsInsurance>) {
      state.currentPet.insurance_list = [...state.currentPet.insurance_list, action.payload];
    },
    updatePetInsuranceInList(state, action: PayloadAction<PetsInsurance>) {
      const updatedList = state.currentPet.insurance_list.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }

        return item;
      });
      state.currentPet.insurance_list = updatedList;
    },
    deletePetInsuranceFromList(state, action: PayloadAction<{ id: number }>) {
      state.currentPet.insurance_list = state.currentPet.insurance_list.filter(
        (item) => item.id !== action.payload.id,
      );
    },
    // ------------------ pet vaccine ------------------
    addCreatedPetVaccineToList(state, action: PayloadAction<PetsVaccine>) {
      state.currentPet.vaccines = [...state.currentPet.vaccines, action.payload];
    },
    updatePetVaccineInList(state, action: PayloadAction<PetsVaccine>) {
      const updatedList = state.currentPet.vaccines.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }

        return item;
      });
      state.currentPet.vaccines = updatedList;
    },
    deletePetVaccineFromList(state, action: PayloadAction<{ id: number }>) {
      state.currentPet.vaccines = state.currentPet.vaccines.filter((item) => item.id !== action.payload.id);
    },
    // ------------------ gallery pet ------------------
    addGalleryPhoto(state, action: PayloadAction<{ newGalleryPhotos: MediaType[] }>) {
      state.currentPet.photos = action.payload.newGalleryPhotos;
    },
    updateGalleryCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentPet.cover_photo = action.payload.coverPhoto;
    },
    removeGalleryCoverPhoto(
      state,
      action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>,
    ) {
      const deleteCoverIndex = state.currentPet.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentPet.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentPet.photos.length) {
        const { photos } = changeCoverPhoto(state.currentPet.photos);
        const hasCoverPhoto = state.currentPet.photos.filter((photo) => photo.is_cover);

        state.currentPet.photos = [...photos];
        if (!hasCoverPhoto.length) {
          state.currentPet.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentPet.cover_photo = null;
    },
    // ------------------ files pet ------------------
    addPetDocuments(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentPet.documents = action.payload.newDocuments;
    },
    // ------------------ share pet ------------------
    setAssignPeopleLists(state, action: PayloadAction<{ peopleLists: AssignPeopleSelectValueModel[] }>) {
      state.share.sharePeopleList = action.payload.peopleLists;
    },
    setSelectedUser(state, action: PayloadAction<{ selectUser: AssignPeopleSelectValueModel | null }>) {
      state.share.selectUser = action.payload.selectUser;
    },
    addShareUser(state, action: PayloadAction<{ shareUser: AssignPeopleSelectValueModel }>) {
      state.currentPet.users.push(action.payload.shareUser);
    },
    deletePetShareUser(state, action: PayloadAction<{ shareUserID: number }>) {
      const index = state.currentPet.users.findIndex((user: any) => user.id === action.payload.shareUserID);
      if (index !== -1) state.currentPet.users.splice(index, 1);
    },
    updateShareUser(
      state,
      action: PayloadAction<{ userRole: AssignPeoplePermissionsEnum; shareUserID: number }>,
    ) {
      const index = state.currentPet.users.findIndex((user: any) => user.id === action.payload.shareUserID);
      if (index !== -1) state.currentPet.users[index].role = action.payload.userRole;
    },
    // ------------------ create pet ------------------
    setPetCreateType: (state, action: PayloadAction<PetTypeEnum>) => {
      state.createPet.petType = action.payload;
    },
    setPetCreateWay: (state, action: PayloadAction<WayToPetInfo>) => {
      state.createPet.wayToEnterPetInfo = action.payload;
    },
    setPetCoverPhoto: (state, action: PayloadAction<{ cover_photo: MediaType | null }>) => {
      if (state.createPet.draftItem) {
        state.createPet.draftItem.cover_photo = action.payload.cover_photo;
      }
    },
    setPetCreateDraftItem: (state, action: PayloadAction<RootPetsData | null>) => {
      state.createPet.draftItem = action.payload;
      if (action.payload) {
        state.createPet.petType = action.payload.type;
      }
    },
    updatePetDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createPet.draftItem = { ...state.createPet.draftItem, ...action.payload };
    },
    setPetCreateCurrentStep: (state, action: PayloadAction<CreatePetStepsEnum>) => {
      state.createPet.currentStep = action.payload;
      state.createPet.isCanChangeCurrentStep = true;
    },
    changePetCreateFormStepperFromDraftItem: (state, action: PayloadAction<RootPetItemsDataStep[]>) => {
      const enabledSteps = new Set([
        CreatePetStepsEnum.preStepPetType,
        CreatePetStepsEnum.preStepSelectMethod,
      ]);
      const isIncludedAiStep = action.payload.some((item) => item.step === CreatePetStepsEnum.preStepAI);

      state.createPet.wayToEnterPetInfo = isIncludedAiStep ? WayToPetInfo.ai : WayToPetInfo.manual;

      action.payload.forEach(({ step }) => {
        const currentStep = state.createPet.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];
      if (lastStepInPayload) {
        state.createPet.currentStep =
          createFormStepper[createFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1];
      }
    },
    setPetCreateFormStepPassed: (
      state,
      action: PayloadAction<{ step: CreatePetStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.createPet.formStepper[step]) {
        state.createPet.formStepper[step].isPassed = isPassed;
      }
    },
    setPetCreateFormStepDisabled: (
      state,
      action: PayloadAction<{ step: CreatePetStepsEnum; isDisabled?: boolean }>,
    ) => {
      const { step, isDisabled = true } = action.payload;
      if (state.createPet.formStepper[step]) {
        state.createPet.formStepper[step].isDisabled = isDisabled;
      }
    },
    setPetCreateFormStepSkip: (
      state,
      action: PayloadAction<{ step: CreatePetStepsEnum; isSkip?: boolean }>,
    ) => {
      const { step, isSkip = true } = action.payload;
      if (state.createPet.formStepper[step]) {
        state.createPet.formStepper[step].isSkip = isSkip;
      }
    },
    setPetCreateCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.createPet.isCanChangeCurrentStep = action.payload;
    },
    changePetCreateFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: CreatePetStepsEnum[];
        passedStep: RootPetItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.createPet.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    // resetPetStore

    resetCreateStore: (state) => {
      state.createPet = {
        formStepper: formStepper,
        currentStep: CreatePetStepsEnum.preStepPetType,
        petType: PetTypeEnum.Custom,
        draftItem: null,
        isCanChangeCurrentStep: true,
        wayToEnterPetInfo: WayToPetInfo.manual,
      };
    },

    updatePetCoverPhotoWS(state, action: PayloadAction<{ newCoverPhoto: MediaType; petID: number }>) {
      const index = state.pets.data.findIndex((pet) => pet.id === action.payload.petID);

      if (index !== -1) state.pets.data[index].cover_photo = action.payload.newCoverPhoto;
    },
  },

  extraReducers: ({ addCase }) => {
    // ------------------ main page ------------------
    addCase(getPets.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.pets.data = payload.data;
        state.pets.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.pets.data.push(...payload.data);
        state.pets.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });
    // ------------------ web-socket ------------------
    addCase(updatePetWS.fulfilled, (state, action: PayloadAction<{ petWS: RootPetsData }>) => {
      if (!!state.pets.data.length) {
        const index = state.pets.data.findIndex((pet) => pet.id === action.payload.petWS.id);

        if (index !== -1) state.pets.data[index] = action.payload.petWS;
      }
    });
    addCase(addPetWS.fulfilled, (state, action: PayloadAction<{ addPetWS: RootPetsData }>) => {
      const index = state.pets.data.findIndex((pet) => pet.id === action.payload.addPetWS.id);

      if (index !== -1) {
        state.pets.data[index] = action.payload.addPetWS;
      } else {
        state.pets.data = !!state.pets.data.length
          ? [action.payload.addPetWS, ...state.pets.data]
          : [action.payload.addPetWS];
      }
    });
    addCase(acceptedPetWS.fulfilled, (state, action: PayloadAction<{ acceptedPetWS: RootPetsData }>) => {
      const index = state.pets.data.findIndex((pet) => pet.id === action.payload.acceptedPetWS.id);
      state.pets.data.splice(index, 1);

      const pending = state.pets.data.filter((pet) => pet.current.confirm_status === StatusTransport.pending);
      const general = state.pets.data.filter((pet) => pet.current.confirm_status !== StatusTransport.pending);

      state.pets.data = [...pending, action.payload.acceptedPetWS, ...general];
    });
  },
});

export const {
  setPetsFilterParams,
  setLoadingPets,
  isEmptyDataPetsWithFilter,
  removeCurrentPet,
  setPetDataWithNewPosition,
  clearPetsState,
  resetCreateStore,
  setPetCreateType,
  setPetCreateDraftItem,
  changePetCreateFormStepperFromDraftItem,
  setPetCreateCurrentStep,
  setPetCreateFormStepPassed,
  clearPetsFilters,
  setPetCreateFormStepDisabled,
  setPetCreateFormStepSkip,
  setPetCreateCanChangeCurrentStep,
  setPetCreateWay,
  changePetCreateFormStepperPassedDisableSteps,
  setPetCoverPhoto,
  setCurrentPet,
  setEditMode,
  addGalleryPhoto,
  updateGalleryCoverPhoto,
  removeGalleryCoverPhoto,
  addPetDocuments,
  setAssignPeopleLists,
  setSelectedUser,
  addShareUser,
  deletePetShareUser,
  updateShareUser,
  updatePetCoverPhotoWS,
  updatePetDraftItem,
  addCreatedPetInsuranceToList,
  updatePetInsuranceInList,
  deletePetInsuranceFromList,
  addCreatedPetVaccineToList,
  updatePetVaccineInList,
  deletePetVaccineFromList,
  resetPetState,
} = petsSlice.actions;

export const petsReducer = petsSlice.reducer;
